import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Trash2, Search, Filter, Printer } from 'lucide-react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDevis = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const getAuthHeader = () => {
    const password = localStorage.getItem('idmat_admin');
    return {
      headers: {
        'Authorization': 'Basic ' + btoa('admin:' + password)
      }
    };
  };

  const fetchQuotes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quotes`, getAuthHeader());
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Erreur lors du chargement des devis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleStatusChange = async (quoteId, newStatus) => {
    try {
      await axios.put(`${API}/admin/quotes/${quoteId}`, { status: newStatus }, getAuthHeader());
      toast.success('Statut mis à jour');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (quoteId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce devis ?')) return;
    
    try {
      await axios.delete(`${API}/admin/quotes/${quoteId}`, getAuthHeader());
      toast.success('Devis supprimé');
      fetchQuotes();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      nouveau: 'bg-yellow-100 text-yellow-800',
      en_cours: 'bg-blue-100 text-blue-800',
      traite: 'bg-green-100 text-green-800',
      refuse: 'bg-red-100 text-red-800'
    };
    const labels = {
      nouveau: 'Nouveau',
      en_cours: 'En cours',
      traite: 'Traité',
      refuse: 'Refusé'
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold ${styles[status] || styles.nouveau}`}>
        {labels[status] || 'Nouveau'}
      </span>
    );
  };

  const getServiceLabel = (type) => {
    const labels = {
      reparation: 'Réparation',
      peinture: 'Peinture',
      entretien: 'Entretien'
    };
    return labels[type] || type;
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      `${quote.first_name} ${quote.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.equipment_brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Toaster position="top-right" richColors />
      <div data-testid="admin-devis">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <h1 
            className="text-3xl font-bold text-[#1a1a1a]"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Demandes de Devis ({quotes.length})
          </h1>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou marque..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 focus:border-[#F5C500] focus:outline-none"
              data-testid="status-filter"
            >
              <option value="all">Tous les statuts</option>
              <option value="nouveau">Nouveau</option>
              <option value="en_cours">En cours</option>
              <option value="traite">Traité</option>
              <option value="refuse">Refusé</option>
            </select>
          </div>
        </div>

        {/* Quotes Table */}
        {filteredQuotes.length === 0 ? (
          <div className="bg-white p-8 text-center text-gray-500 shadow-sm">
            Aucune demande de devis trouvée
          </div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Client</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Engin</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <p className="font-medium text-[#1a1a1a]">{quote.first_name} {quote.last_name}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-600">{quote.email}</p>
                        <p className="text-sm text-gray-500">{quote.phone}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-[#1a1a1a]">{quote.equipment_brand}</p>
                        <p className="text-sm text-gray-500">{quote.equipment_model}</p>
                      </td>
                      <td className="px-6 py-4">
                        {getServiceLabel(quote.service_type)}
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={quote.status}
                          onChange={(e) => handleStatusChange(quote.id, e.target.value)}
                          className="text-sm border border-gray-200 px-2 py-1 focus:border-[#F5C500] focus:outline-none"
                          data-testid={`status-select-${quote.id}`}
                        >
                          <option value="nouveau">Nouveau</option>
                          <option value="en_cours">En cours</option>
                          <option value="traite">Traité</option>
                          <option value="refuse">Refusé</option>
                        </select>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Link
                            to={`/admin/devis/${quote.id}`}
                            className="p-2 hover:bg-gray-100 text-blue-600"
                            title="Voir détails"
                            data-testid={`view-${quote.id}`}
                          >
                            <Eye size={18} />
                          </Link>
                          <button
                            onClick={() => handleDelete(quote.id)}
                            className="p-2 hover:bg-gray-100 text-red-600"
                            title="Supprimer"
                            data-testid={`delete-${quote.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDevis;
